import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "../../../components/Modal/Modal";
import TemplateBodyPreview from "./TemplateBodyPreview";
import { TEMPLATE_VARIABLES, toBackend, toDisplay } from "../templateVariables";
import styles from "./TemplateFormModal.module.css";

const CATEGORY_OPTIONS = [
  { value: "UTILITY", label: "Aviso / Lembrete (recomendado)" },
  { value: "MARKETING", label: "Marketing / Promoção" },
];

const BODY_PLACEHOLDER =
  "Ex: Olá, {{nome}}! Lembrando do seu {{serviço}} amanhã às {{horário}}. Até logo!";

/**
 * Modal de criação/edição de modelo de mensagem.
 *
 * Reusa o `Modal` base (overlay, foco no container, fechar com Esc) e adiciona
 * o cabeçalho/rodapé e a lógica de formulário específicos.
 *
 * Dois modos:
 * - Criação (`template` = null): cria via `onSubmit({ displayName, bodyText, category })`.
 * - Edição (`template` definido, fluxo "Corrigir e reenviar" de REJECTED):
 *   `displayName` vira somente-leitura (o slug não pode mudar); o `bodyText` é
 *   pré-preenchido via `toDisplay` e editável.
 *
 * O componente NÃO chama a API: delega ao `onSubmit`, que recebe o `bodyText`
 * já convertido para o formato backend ({{1}}/{{2}}/{{3}}).
 */
export default function TemplateFormModal({
  isOpen,
  template = null,
  submitting = false,
  onSubmit,
  onClose,
}) {
  const isEditMode = Boolean(template);
  const titleId = useId();
  const displayNameId = useId();
  const bodyTextId = useId();
  const categoryId = useId();
  const displayNameInputRef = useRef(null);
  const bodyTextInputRef = useRef(null);

  const [displayName, setDisplayName] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [touched, setTouched] = useState({ displayName: false, bodyText: false });

  // (Re)inicializa o formulário sempre que o modal abre ou o template muda.
  useEffect(() => {
    if (!isOpen) return;
    setDisplayName(template?.displayName ?? "");
    setBodyText(template ? toDisplay(template.bodyText ?? "") : "");
    setCategory(template?.category ?? "UTILITY");
    setTouched({ displayName: false, bodyText: false });
  }, [isOpen, template]);

  // Foco inicial: no modo edição o nome é read-only, então focamos o textarea.
  useEffect(() => {
    if (!isOpen) return;
    const target = isEditMode
      ? bodyTextInputRef.current
      : displayNameInputRef.current;
    // Pequeno defer para garantir que o Modal já montou o container.
    const raf = requestAnimationFrame(() => target?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isOpen, isEditMode]);

  const trimmedName = displayName.trim();
  const trimmedBody = bodyText.trim();
  const nameError = trimmedName.length === 0 ? "Dê um nome ao seu modelo." : "";
  const bodyError =
    trimmedBody.length === 0 ? "Escreva o texto da mensagem." : "";
  const isValid = !nameError && !bodyError;

  const examplesLine = useMemo(
    () =>
      TEMPLATE_VARIABLES.map((v) => `{{${v.alias}}}`).join(", "),
    [],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ displayName: true, bodyText: true });
    if (!isValid || submitting) return;
    onSubmit({
      displayName: trimmedName,
      bodyText: toBackend(trimmedBody),
      category,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="620px" titleId={titleId}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {isEditMode ? "Corrigir e reenviar modelo" : "Criar modelo de mensagem"}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {/* Nome do modelo */}
          <div className={styles.field}>
            <label htmlFor={displayNameId} className={styles.label}>
              Nome do modelo
            </label>
            <input
              id={displayNameId}
              ref={displayNameInputRef}
              type="text"
              className={styles.input}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={() =>
                setTouched((t) => ({ ...t, displayName: true }))
              }
              placeholder="Ex: Lembrete de Agendamento"
              readOnly={isEditMode}
              aria-invalid={touched.displayName && Boolean(nameError)}
              aria-describedby={
                isEditMode ? `${displayNameId}-hint` : undefined
              }
            />
            {isEditMode && (
              <p id={`${displayNameId}-hint`} className={styles.helper}>
                O nome não pode ser alterado em um modelo já criado.
              </p>
            )}
            {touched.displayName && nameError && (
              <p className={styles.error} role="alert">
                {nameError}
              </p>
            )}
          </div>

          {/* Texto da mensagem */}
          <div className={styles.field}>
            <label htmlFor={bodyTextId} className={styles.label}>
              Texto da mensagem
            </label>
            <textarea
              id={bodyTextId}
              ref={bodyTextInputRef}
              className={styles.textarea}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, bodyText: true }))}
              placeholder={BODY_PLACEHOLDER}
              rows={4}
              aria-invalid={touched.bodyText && Boolean(bodyError)}
              aria-describedby={`${bodyTextId}-vars`}
            />
            {touched.bodyText && bodyError && (
              <p className={styles.error} role="alert">
                {bodyError}
              </p>
            )}

            {/* Instruções de variáveis (sempre visíveis) */}
            <div id={`${bodyTextId}-vars`} className={styles.varsBox}>
              <p className={styles.varsTitle}>
                <span aria-hidden="true">💡</span> Você pode usar estas variáveis
                no texto:
              </p>
              <ul className={styles.varsList}>
                {TEMPLATE_VARIABLES.map((v) => (
                  <li key={v.alias}>
                    <code className={styles.varCode}>{`{{${v.alias}}}`}</code>
                    <span className={styles.varDesc}>{v.desc}</span>
                  </li>
                ))}
              </ul>
              <p className={styles.varsExample}>
                Exemplo: “Olá, {`{{nome}}`}! Lembrando do seu {`{{serviço}}`}{" "}
                amanhã às {`{{horário}}`}.”
                <span className={styles.srOnly}> Variáveis disponíveis: {examplesLine}.</span>
              </p>
            </div>
          </div>

          {/* Tipo de mensagem */}
          <div className={styles.field}>
            <label htmlFor={categoryId} className={styles.label}>
              Tipo de mensagem
            </label>
            <select
              id={categoryId}
              className={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Preview em tempo real */}
          <div className={styles.field}>
            <span className={styles.label}>Pré-visualização</span>
            <TemplateBodyPreview text={bodyText} />
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={submitting}
          >
            {submitting ? "Enviando..." : "Enviar para aprovação"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}

TemplateFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  /** Template em edição (fluxo REJECTED). `null` = modo criação. */
  template: PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string,
    bodyText: PropTypes.string,
    category: PropTypes.string,
  }),
  submitting: PropTypes.bool,
  /** Recebe `{ displayName, bodyText (formato backend), category }`. */
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
